import { checkStorageLimit } from '@/features/org/api/check-storage-limit';
import { supabase } from '@/lib/supabase';
import { FileDetails } from '@/shared/components/ui/upload/document-upload-dialog';
import { ProjectDocument } from '@/shared/types/api';

/// ä,ö,ü and maybe more is currently not supported by supabase storage (see: https://github.com/supabase/storage/issues/133)
/// And idea is to uri encode the filename but files with ä,ö,ü will still not work
/// Therefore we normalize the filename and remove diacritics. If we want to preserve the original filename we need to find
/// a different solution or check the support some day again
function sanitizeFilename(filename: string): string {
  return filename
    .normalize('NFD') // Decompose characters with diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-zA-Z0-9.-]/g, '_') // Replace other special chars with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
}

export const generateFilePath = ({
  orgId,
  project,
  userId,
  fileName,
  visibility,
}: {
  orgId: number | undefined;
  project: number | undefined;
  userId: string | undefined;
  fileName: string | undefined;
  visibility: string | undefined;
}): string => {
  if (!orgId || !project || !userId || !fileName || !visibility) {
    throw new Error('Missing parameters');
  }

  const sanitizedFileName = sanitizeFilename(fileName);

  return `org-${orgId}/project-${project}/${visibility}/user-${userId}/${sanitizedFileName}`;
};

export const uploadDocument = async ({
  fileDetails,
  orgId,
}: {
  fileDetails: FileDetails;
  orgId: number;
}): Promise<ProjectDocument> => {
  const filePath = generateFilePath({
    orgId,
    project: fileDetails.project,
    userId: fileDetails.owner,
    fileName: fileDetails.file.name,
    visibility: fileDetails.visibility,
  });

  await checkStorageLimit(orgId, fileDetails.file.size);

  const { data: storageData, error: storageError } = await supabase.storage
    .from('documents')
    .upload(filePath, fileDetails.file);

  if (storageError) {
    throw storageError;
  }

  const insertDto = {
    file_path: storageData?.path,
    project_id: fileDetails.project,
    org_id: orgId,
    creator_id: fileDetails.owner,
    title: fileDetails.title,
    visibility: fileDetails.visibility,
    type: fileDetails.type,
    association: fileDetails.association,
  };

  const { data, error } = await supabase
    .from('project_documents')
    .insert(insertDto)
    .select()
    .single();

  if (error) {
    throw error;
  }

  //TODO kpi reference once implemented

  return data;
};
